// Kampanyalar.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Button, Typography, TextField, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, InputLabel,
  FormControl, Switch, FormControlLabel, Snackbar, Chip, IconButton,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InventoryIcon from "@mui/icons-material/Inventory";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";

const generateCampaignCode = () =>
  `KMP-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;

const getInitialFormData = () => ({
  name: "",
  description: "",
  discountRate: "",
  discountAmount: "",
  startDate: new Date().toISOString().split("T")[0],
  endDate: "",
  code: generateCampaignCode(),
  packages: [],
  isActive: true,
  _id: undefined,
});

const CampaignPage = () => {
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [packages, setPackages] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [formData, setFormData] = useState(getInitialFormData());
  const [campaigns, setCampaigns] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [counts, setCounts] = useState({ active: 0, past: 0, total: 0 });

  const getAuthHeaders = useCallback(() => {
    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token");

    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const filteredRows = Array.isArray(campaigns)
    ? campaigns.filter((row) => {
        const matchesSearch = row.name
          .toLowerCase()
          .includes(searchText.toLowerCase());

        const now = new Date().toISOString().split("T")[0];
        const isPast = row.endDate && row.endDate < now;

        const matchesStatus =
          statusFilter === "" ||
          (statusFilter === "active" && row.isActive && !isPast) ||
          (statusFilter === "past" && isPast);

        return matchesSearch && matchesStatus;
      })
    : [];

  const updateExpiredCampaigns = useCallback(
    async (list) => {
      const now = new Date().toISOString().split("T")[0];

      const expiredToUpdate = list.filter(
        (c) => c.isActive && c.endDate && c.endDate < now
      );

      for (const campaign of expiredToUpdate) {
        try {
          await axios.put(
            `/api/campaigns/${campaign._id}`,
            {
              ...campaign,
              packages: campaign.packages.map((p) =>
                typeof p === "object" ? p._id : p
              ),
              isActive: false,
            },
            {
              headers: getAuthHeaders(),
            }
          );

          console.log(`Kampanya pasifleştirildi: ${campaign.name}`);
        } catch (err) {
          console.error(
            `Kampanya pasifleştirme hatası: ${campaign.name}`,
            err
          );
        }
      }
    },
    [getAuthHeaders]
  );

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get("/api/campaigns", {
        headers: getAuthHeaders(),
      });

      const all = res.data.data;
      const now = new Date().toISOString().split("T")[0];

      const active = all.filter(
        (c) => c.isActive && (!c.endDate || c.endDate >= now)
      ).length;

      const past = all.filter((c) => c.endDate && c.endDate < now).length;

      setCampaigns(all);
      setCounts({
        active,
        past,
        total: all.length,
      });

      updateExpiredCampaigns(all);
    } catch (err) {
      console.error("Kampanya listeleme hatası:", err);
    }
  }, [getAuthHeaders, updateExpiredCampaigns]);

  const fetchPackages = useCallback(async () => {
    try {
      const res = await axios.get("/api/packages", {
        headers: getAuthHeaders(),
      });
      setPackages(res.data.data);
    } catch (err) {
      console.error("Paketleri alırken hata:", err);
    }
  }, [getAuthHeaders]);

  const handleSubmit = async () => {
    const form = new FormData();
    form.append("name", formData.name || "");
    form.append("description", formData.description || "");
    form.append("discountRate", formData.discountRate?.toString() || "");
    form.append("discountAmount", formData.discountAmount?.toString() || "");
    form.append("startDate", formData.startDate);
    form.append("endDate", formData.endDate);
    form.append("code", formData.code || generateCampaignCode());
    form.append("packages", JSON.stringify(formData.packages));
    form.append("isActive", formData.isActive.toString());

    try {
      if (formData._id) {
        await axios.put(`/api/campaigns/${formData._id}`, form, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        });

        setSnackbar({
          open: true,
          message: "Kampanya güncellendi!",
          severity: "success",
        });
      } else {
        await axios.post("/api/campaigns", form, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        });

        setSnackbar({
          open: true,
          message: "Kampanya eklendi!",
          severity: "success",
        });
      }

      setOpen(false);
      setViewMode(false);
      setFormData(getInitialFormData());
      fetchCampaigns();
    } catch (err) {
      console.error("Kampanya ekleme hatası:", err);
      setSnackbar({
        open: true,
        message: "Kampanya eklenemedi!",
        severity: "error",
      });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleClose = () => {
    setOpen(false);
    setViewMode(false);
  };

  const handlePackageSelect = (e) => {
    setFormData({ ...formData, packages: e.target.value });
  };

  const handleEdit = (row) => {
    setFormData({
      name: row.name,
      description: row.description,
      discountRate: row.discountRate?.toString() || "",
      discountAmount: row.discountAmount?.toString() || "",
      startDate: row.startDate?.split("T")[0] || "",
      endDate: row.endDate?.split("T")[0] || "",
      code: row.code,
      packages: row.packages.map((p) => (typeof p === "object" ? p._id : p)),
      isActive: row.isActive,
      _id: row._id,
    });
    setOpen(true);
    setViewMode(false);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/api/campaigns/${selectedCampaignId}`, {
        headers: getAuthHeaders(),
      });

      setSnackbar({
        open: true,
        message: "Kampanya silindi",
        severity: "success",
      });

      fetchCampaigns();
    } catch (err) {
      console.error("Silme hatası:", err);
      setSnackbar({
        open: true,
        message: "Silme başarısız!",
        severity: "error",
      });
    } finally {
      setConfirmDeleteOpen(false);
      setSelectedCampaignId(null);
    }
  };

  const totalPrice = formData.packages.reduce((acc, id) => {
    const found = packages.find((p) => p._id === id);
    return acc + (found?.price || 0);
  }, 0);

  const discountedPrice = formData.discountRate
    ? totalPrice * (1 - parseFloat(formData.discountRate) / 100)
    : totalPrice - (parseFloat(formData.discountAmount) || 0);

  const handleView = (row) => {
    setFormData({
      name: row.name,
      description: row.description,
      discountRate: row.discountRate?.toString() || "",
      discountAmount: row.discountAmount?.toString() || "",
      startDate: row.startDate?.split("T")[0] || "",
      endDate: row.endDate?.split("T")[0] || "",
      code: row.code,
      packages: row.packages.map((p) => (typeof p === "object" ? p._id : p)),
      isActive: row.isActive,
      _id: row._id,
    });
    setOpen(true);
    setViewMode(true);
  };

  useEffect(() => {
    fetchCampaigns();
    fetchPackages();
  }, [fetchCampaigns, fetchPackages]);

  return (
    <Box p={3}>
      <Box display="flex" gap={2} mb={3} justifyContent={"center"}>
        {[
          {
            icon: <DoneAllIcon color="success" />,
            label: "Aktif Kampanyalar",
            value: counts.active,
          },
          {
            icon: <EventBusyIcon color="error" />,
            label: "Geçmiş Kampanyalar",
            value: counts.past,
          },
          {
            icon: <InventoryIcon color="primary" />,
            label: "Toplam Kampanya",
            value: counts.total,
          },
        ].map((card, i) => (
          <Box
            key={i}
            p={2}
            boxShadow={2}
            borderRadius={2}
            bgcolor="white"
            width={250}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            {card.icon}
            <Typography variant="h6" mt={1}>
              {card.label}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {card.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Button
        startIcon={<AddIcon />}
        variant="contained"
        sx={{ mt: 3, mb: 3 }}
        onClick={() => {
          setFormData(getInitialFormData());
          setViewMode(false);
          setOpen(true);
        }}
      >
        Yeni Kampanya Ekle
      </Button>

      <Box display="flex" gap={2} mb={2}>
        <TextField
          label="Kampanya Ara"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Durum</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Durum"
          >
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value="active">Aktif</MenuItem>
            <MenuItem value="past">Geçmiş</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ width: "97%", overflowX: "auto", mb: 2 }}>
        <DataGrid
          rows={filteredRows}
          getRowId={(row) => row.code}
          columns={[
            { field: "name", headerName: "Kampanya Adı", flex: 1 },
            { field: "description", headerName: "Açıklama", flex: 1 },
            { field: "code", headerName: "Kod", flex: 0.6 },
            { field: "discountRate", headerName: "İnd. Oranı %", flex: 0.6 },
            { field: "discountAmount", headerName: "İnd. Tutar ₺", flex: 0.6 },
            {
              field: "startDate",
              headerName: "Başlangıç",
              flex: 0.8,
              renderCell: (params) =>
                new Date(params.value).toLocaleDateString("tr-TR"),
            },
            {
              field: "endDate",
              headerName: "Bitiş",
              flex: 0.8,
              renderCell: (params) =>
                params.value
                  ? new Date(params.value).toLocaleDateString("tr-TR")
                  : "-",
            },
            {
              field: "isActive",
              headerName: "Durum",
              flex: 0.6,
              renderCell: (params) => (params.value ? "Aktif" : "Pasif"),
            },
            {
              field: "actions",
              headerName: "İşlemler",
              flex: 1,
              renderCell: (params) => (
                <Box
                  display="flex"
                  gap={1}
                  alignItems="center"
                  justifyContent="center"
                  height={"100%"}
                  width={"100%"}
                >
                  <IconButton size="small" onClick={() => handleView(params.row)}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleEdit(params.row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedCampaignId(params.row._id);
                      setConfirmDeleteOpen(true);
                    }}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ),
            },
          ]}
          autoHeight
        />
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {viewMode
            ? "Kampanya Detayı"
            : formData._id
              ? "Kampanya Düzenle"
              : "Yeni Kampanya Ekle"}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            disabled={viewMode}
            label="Kampanya Adı"
            fullWidth
            margin="dense"
            name="name"
            value={formData.name}
            onChange={handleChange}
          />
          <TextField
            disabled={viewMode}
            label="Açıklama"
            fullWidth
            margin="dense"
            name="description"
            value={formData.description}
            onChange={handleChange}
          />

          <Box display="flex" gap={2} mb={2} mt={1}>
            <TextField
              disabled={viewMode}
              label="İndirim Oranı %"
              type="number"
              name="discountRate"
              value={formData.discountRate}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  discountRate: e.target.value,
                  discountAmount: "",
                })
              }
              fullWidth
            />
            <TextField
              disabled={viewMode}
              label="İndirim Tutarı ₺"
              type="number"
              name="discountAmount"
              value={formData.discountAmount}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  discountAmount: e.target.value,
                  discountRate: "",
                })
              }
              fullWidth
            />
          </Box>

          <Box display="flex" gap={2} mb={2} mt={1}>
            <TextField
              disabled={viewMode}
              label="Başlangıç Tarihi"
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              disabled={viewMode}
              label="Bitiş Tarihi"
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <TextField
            label="Kampanya Kodu"
            fullWidth
            margin="dense"
            name="code"
            value={formData.code}
            disabled
          />

          <Tooltip title="Kopyala">
            <IconButton
              disabled={viewMode}
              onClick={() => {
                navigator.clipboard.writeText(formData.code);
                setSnackbar({
                  open: true,
                  message: "Kampanya kodu kopyalandı!",
                  severity: "info",
                });
              }}
              sx={{ right: 0 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {viewMode ? (
            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
              {formData.packages.map((id) => {
                const pkg = packages.find((p) => p._id === id);
                return <Chip key={id} label={pkg?.name || id} />;
              })}
            </Box>
          ) : (
            <FormControl fullWidth margin="dense">
              <InputLabel>Paketler</InputLabel>
              <Select
                multiple
                name="packages"
                value={formData.packages}
                onChange={handlePackageSelect}
                renderValue={(selected) =>
                  selected
                    .map((id) => {
                      const found = packages.find((p) => p._id === id);
                      return found?.name || id;
                    })
                    .join(", ")
                }
              >
                {packages.map((pkg) => (
                  <MenuItem key={pkg._id} value={pkg._id}>
                    {pkg.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box mt={2}>
            <Typography variant="subtitle2">
              Toplam Fiyat: ₺{totalPrice.toFixed(2)}
            </Typography>
            <Typography variant="subtitle2">
              İndirimli Fiyat: ₺{discountedPrice.toFixed(2)}
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch
                disabled={viewMode}
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
              />
            }
            label={formData.isActive ? "Aktif" : "Pasif"}
            sx={{ mt: 1 }}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              setViewMode(false);
            }}
          >
            Kapat
          </Button>

          {!viewMode && (
            <Button onClick={handleSubmit} variant="contained">
              Kaydet
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Kampanya Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Bu kampanyayı silmek istediğinize emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Vazgeç</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Evet, Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignPage;